import type { ChartData, ChartOptions } from "chart.js";

type Metric = 'carbon' | 'cost';

function barColor(percent: number): string {
  if (percent <= 80)  return '#4caf50';   // >20% better than 2020
  if (percent <= 100) return '#8bc34a';   // slightly better
  if (percent <= 120) return '#ff9800';   // slightly worse
  return '#f44336';                       // >20% worse
}

interface IChartOption { metric: Metric; }

export function carbonCostChartOption({ metric }: IChartOption): ChartOptions {
  const title = metric === 'carbon' ? 'Carbon intensity' : 'Wholesale cost';
  return {
    animation: false,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: title,
        color: 'rgba(255,255,255,0.88)',
        font: { size: 11 },
        padding: { bottom: 4 },
      },
      annotation: {
        annotations: {
          baseline: {
            type: 'line',
            scaleID: 'y',
            value: 100,
            borderColor: 'rgba(255,255,255,0.65)',
            borderWidth: 2,
            borderDash: [6, 4],
            label: {
              enabled: true,
              content: '2020',
              color: 'rgba(255,255,255,0.85)',
              backgroundColor: 'rgba(0,35,60,0.85)',
              font: { size: 9, weight: 'bold' },
              position: 'end',
            }
          }
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          color: 'rgba(255,255,255,0.85)',
          font: { size: 10 },
        },
      },
      y: {
        min: 0,
        title: {
          display: true,
          text: '% of 2020',
          font: { size: 10 },
          color: 'rgba(255,255,255,0.65)',
        },
        grid: { color: 'rgba(255,255,255,0.08)' }
      }
    }
  };
}

interface ICarbonCostChartData {
  annualConst: any;
  optionsData: any;
  metric: Metric;
}
export function carbonCostChartData({
  annualConst,
  optionsData,
  metric
}: ICarbonCostChartData): ChartData {
  const {
    totalAnnualCarbon,
    totalAnnualCarbonOriginal,
    totalAnnualCost,
    totalAnnualCostOriginal
  } = annualConst;

  // Current value as a percentage of the 2020 baseline. Below 100 = better
  // than 2020, above 100 = worse.
  const netCarbon = Number(
    ((totalAnnualCarbon / totalAnnualCarbonOriginal) * 100).toFixed(1)
  );
  const netCost = Number(
    ((totalAnnualCost / totalAnnualCostOriginal) * 100).toFixed(1)
  );

  if (metric === 'carbon') {
    return {
      labels: [`${annualConst.totalAnnualCarbon.toFixed(3)} t/MWh`],
      datasets: [{
        label: 'Carbon vs 2020',
        data: [netCarbon],
        backgroundColor: [barColor(netCarbon)],
        borderColor: 'transparent',
        borderRadius: 6,
        barPercentage: 0.6,
      }]
    };
  }

  return {
    labels: [`${annualConst.totalAnnualCost.toFixed(1)} ${optionsData.currency}/MWh`],
    datasets: [{
      label: 'Cost vs 2020',
      data: [netCost],
      backgroundColor: [barColor(netCost)],
      borderColor: 'transparent',
      borderRadius: 6,
      barPercentage: 0.6,
    }]
  };
}
